import React, { Component, useContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import hoistStatics from 'hoist-non-react-statics';
import warn from '../warn';
import { TSymbolProp, defaultLocale } from './consts';
import { createTLoader } from './TLoader';

export default createContext => {
  const LocaleContext = createContext({ locale: defaultLocale });
  const setComponentRefSymbol = '$$__BIM360--LocalizedComponent--setComponentRef__$$';

  const withLocale = (WrappedComponent, customTLoader) => {
    const componentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    const loadT = createTLoader({ componentName, customTLoader });

    class LocalizedComponent extends Component {
      constructor(props) {
        super(props);
        this.locale = props.locale || defaultLocale;

        // This is a cheap replacement for Proxy.
        // Instead of checking if `T` has changed in `componentWillReceiveProps` of each localized component,
        // we create a fake one and just change the prototype during run time.
        // Eventually there's gonna be a change in the props because of `locale`, causing a render.
        // So even though `fakeT` won't change reference a render cycle will happen reevaluating all the translations.
        this.fakeT = {};
        this.TProp = { [TSymbolProp]: this.fakeT };
        Object.setPrototypeOf(this.fakeT, loadT(this.locale));

        this[setComponentRefSymbol] = this[setComponentRefSymbol].bind(this);

        this.state = {
          locale: props.locale
        };
      }

      /**
       * These lifecycle methods exists to support cases where the consumers aren't using `LocaleProvider` to provide the current locale to the components.
       * @param nextProps
       */
      UNSAFE_componentWillReceiveProps(nextProps) {
        if (this.props.locale !== nextProps.locale) {
          this.setState({ locale: nextProps.locale });
          warn(
            `You're using the 'locale' prop directly, please use \`LocaleProvider\` instead. See https://git.autodesk.com/BIM360/bim360-matrix-react-components/pull/171 for the usage.`
          );
        }
      }

      nonReactLifecycleMethods(method) {
        return (
          [
            'constructor',
            'render',
            'autobind',
            'childContextTypes',
            'contextTypes',
            'displayName',
            'forceUpdate',
            'getChildContext',
            'getDefaultProps',
            'getDOMNode',
            'getInitialState',
            'isMounted',
            'mixins',
            'propTypes',
            'replaceProps',
            'replaceState',
            'setProps',
            'setState',
            'statics',
            'updateComponent',
            'componentWillUpdate',
            'componentDidUpdate',
            'componentWillMount',
            'componentDidMount',
            'componentWillUnmount',
            'shouldComponentUpdate',
            'componentWillReceiveProps',
            'UNSAFE_componentWillMount',
            'UNSAFE_componentWillReceiveProps',
            'UNSAFE_componentWillUpdate'
          ].indexOf(method) === -1
        );
      }

      [setComponentRefSymbol](ref) {
        if (ref) {
          Object.getOwnPropertyNames(Object.getPrototypeOf(ref))
            .filter(this.nonReactLifecycleMethods)
            .forEach(internalRefMethod => {
              if (!(internalRefMethod in this)) {
                if (typeof ref[internalRefMethod] === 'function') {
                  this[internalRefMethod] = (...args) => ref[internalRefMethod](...args);
                } else {
                  Object.defineProperty(this, internalRefMethod, {
                    enumerable: false,
                    get: () => ref[internalRefMethod],
                    set: value => (ref[internalRefMethod] = value)
                  });
                }
              }
            });
        }
      }

      render() {
        return (
          <LocaleContext.Consumer>
            {context => {
              const desiredLocale =
                this.state.locale ||
                (this.locale !== context.locale ? context.locale : this.locale);

              if (this.locale !== desiredLocale) {
                const T = loadT(desiredLocale);
                T && Object.setPrototypeOf(this.fakeT, T);
                this.locale = desiredLocale;
              }
              return (
                <WrappedComponent
                  {...this.props}
                  {...this.TProp}
                  key="WrappedComponent"
                  locale={desiredLocale}
                  ref={this[setComponentRefSymbol]}
                />
              );
            }}
          </LocaleContext.Consumer>
        );
      }
    }

    LocalizedComponent.displayName = `LocalizedComponent(${componentName})`;
    LocalizedComponent.propTypes = { ...WrappedComponent.propTypes, locale: PropTypes.string }; //override the required locale propType in the WrappedComponent
    LocalizedComponent.defaultProps = WrappedComponent.defaultProps;

    return hoistStatics(LocalizedComponent, WrappedComponent);
  };

  class LocaleProvider extends Component {
    render() {
      return (
        <LocaleContext.Provider value={{ locale: this.props.locale }}>
          {this.props.children}
        </LocaleContext.Provider>
      );
    }
  }

  LocaleProvider.propTypes = {
    children: PropTypes.any,
    locale: PropTypes.string
  };

  LocaleProvider.defaultProps = {
    locale: defaultLocale
  };

  const useLocale = customTLoader => {
    const { locale } = useContext(LocaleContext);
    const customTLoaderRef = useRef(customTLoader);
    const T = useMemo(
      () => createTLoader({ componentName: '', customTLoader: customTLoaderRef.current })(locale),
      [locale]
    );
    return { T, locale };
  };

  return {
    withLocale,
    LocaleProvider,
    useLocale
  };
};
